/** "What the feed sent" (plan §3, §4.5): the booking's feed fingerprint — which `<user>`
 *  attributes, child elements, tag_context keys and guest attributes the feed's import
 *  sent, in which mode, from which source with which update permissions — and the latest
 *  feed run's stored XML sliced to the booking. The record editor's restech verdicts are
 *  built from exactly this, so it is where "why does the editor say Ventrata will undo
 *  this?" is answered.
 *
 *  `recorded` at import, `backfilled` just now from the latest stored XML, or `missing`
 *  with the reason (the verdicts then come from the static rules alone). A party member
 *  shows its lead's — the feed addresses the lead only. Times in the tour's zone,
 *  labelled. */
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Alert, Box, Button, Link, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import {
  useBookingFeed,
  type BookingFeed,
  type BookingFeedDocument,
  type BookingFeedFingerprint,
} from '@/api/bookingActions';
import { DocumentViewer } from '@/components/DocumentViewer/DocumentViewer';
import { Field } from '@/components/ReadView';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import { importIdActions } from '@/features/import-runs/importRunLinks';
import { consoleRouteFor } from '@/utils/gridLinks';
import { formatZoned } from '@/utils/zonedTime';

import { MONO_SX } from '../panels/displays';

import { TabSection } from './TabSection';
import { TabSkeleton } from './TabSkeleton';

export const FEED_SECTION_ID = 'booking-integration-feed';

/** Why there is no fingerprint / no document, in the operator's words. */
const REASONS: Record<string, string> = {
  no_ext_id: 'the booking carries no feed id (ext_id)',
  no_feed_run: 'no feed run has imported it',
  no_stored_xml: "the feed run's stored XML is gone (expired or never stored)",
  too_large: 'the stored XML is too large to read here',
  timeout: 'reading the stored XML took too long',
  unparseable: 'the stored XML could not be parsed',
  unsupported_rev: "the stored XML's format revision is not supported",
  not_in_document: "the booking is not in the latest feed run's XML",
  error: 'reading it failed',
};
const reasonText = (code: string | null | undefined) => (code ? (REASONS[code] ?? code) : 'no reason given');

const yesNo = (v: boolean) => (v ? 'Yes' : 'No');

function Note({ icon, children }: { icon?: ReactNode; children: ReactNode }) {
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      component="p"
      sx={{ m: 0, display: 'flex', gap: 0.75, alignItems: 'flex-start' }}
    >
      {icon ?? <InfoOutlinedIcon aria-hidden sx={{ fontSize: 16, mt: '2px', flex: 'none' }} />}
      <span>{children}</span>
    </Typography>
  );
}

function RunLink({ id, tenantId }: { id: string; tenantId: number | null }) {
  const href = consoleRouteFor('import_run', id);
  const short = (
    <Box component="span" sx={MONO_SX}>
      {id.slice(0, 8)}…
    </Box>
  );
  return (
    <ValueActions actions={importIdActions(id, { tenantId })}>
      {href ? (
        <Link component={RouterLink} to={href} underline="hover" aria-label={`Import run ${id}`}>
          {short}
        </Link>
      ) : (
        short
      )}
    </ValueActions>
  );
}

/** One "sent" row: the vocabulary as the feed's XML spells it. */
function SentRow({ label, names, other }: { label: string; names: string[]; other?: string[] }) {
  return (
    <>
      <Typography variant="caption" color="text.secondary" component="dt" sx={{ pt: '2px' }}>
        {label}
      </Typography>
      <Box component="dd" sx={{ m: 0, minWidth: 0 }}>
        {names.length === 0 && !other?.length ? (
          <Typography variant="body2" color="text.disabled">
            none
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'baseline' }}>
            {names.map((n) => (
              <Box
                key={n}
                component="code"
                sx={{ ...MONO_SX, fontSize: 12, px: 0.5, borderRadius: 0.5, bgcolor: 'action.hover', color: 'text.primary' }}
              >
                {n}
              </Box>
            ))}
            {other && other.length > 0 && (
              <Typography variant="caption" color="text.secondary" component="span">
                also{' '}
                <Box component="span" sx={MONO_SX}>
                  {other.join(', ')}
                </Box>
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </>
  );
}

function Fingerprint({ fp, tz, tenantId }: { fp: BookingFeedFingerprint; tz: string; tenantId: number | null }) {
  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(176px, 1fr))',
          columnGap: 2,
          '& > *': { minWidth: 0 },
        }}
      >
        <Field label="Source" value={fp.source_id ? `${fp.source_id}${fp.origin ? ` · ${fp.origin}` : ''}` : fp.origin} />
        <Field label="Import mode" value={fp.mode ? fp.mode.toUpperCase() : null} />
        <Field label="Recorded" value={formatZoned(fp.recorded_ts, tz)} />
        <Field label="Recorded by run" value={<RunLink id={fp.import_run_id} tenantId={tenantId} />} />
        <Field label="May update bookings" value={yesNo(fp.permissions.update_bookings)} />
        <Field label="May update guests" value={yesNo(fp.permissions.update_users)} />
      </Box>
      <Box
        component="dl"
        aria-label="What the feed sent"
        sx={{
          m: 0,
          display: 'grid',
          gridTemplateColumns: 'minmax(120px, max-content) minmax(0, 1fr)',
          columnGap: 2,
          rowGap: 1,
          alignItems: 'start',
        }}
      >
        <SentRow label="Booking attributes" names={fp.booking_attrs.map((n) => `@${n}`)} />
        <SentRow label="Child elements" names={fp.booking_children.map((n) => `<${n}>`)} />
        <SentRow label="tag_context keys" names={fp.tag_context_keys} other={fp.tag_context_other_keys} />
        <SentRow label="Guest attributes" names={fp.user_attrs.map((n) => `@${n}`)} />
      </Box>
    </>
  );
}

function Document({ doc, bookingId }: { doc: BookingFeedDocument; bookingId: number }) {
  if (doc.status !== 'ok' || !doc.xml) {
    return <Note>No stored XML to show: {reasonText(doc.status === 'ok' ? 'error' : doc.status)}.</Note>;
  }
  const notes = [
    doc.collateral.length > 0
      ? `Also kept: the other bookings of its order or package (${doc.collateral.join(', ')}).`
      : null,
    doc.truncated ? 'Cut at 512 KiB.' : null,
  ].filter(Boolean);
  return (
    <DocumentViewer
      text={doc.xml}
      formatHint="xml"
      downloadName={`booking-${bookingId}-feed`}
      copyMessage="Feed XML copied"
      emptyMessage="The stored XML is empty."
      leading={
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Stored XML, sliced to this booking
        </Typography>
      }
      {...(notes.length > 0
        ? {
            beforeViewer: (
              <Typography variant="caption" color="text.secondary">
                {notes.join(' ')}
              </Typography>
            ),
          }
        : {})}
    />
  );
}

function StatusNote({ feed, tz }: { feed: BookingFeed; tz: string }) {
  if (feed.fingerprint_status === 'missing') {
    return (
      <Note icon={<WarningAmberIcon aria-hidden sx={{ fontSize: 16, mt: '2px', flex: 'none', color: 'warning.main' }} />}>
        Not recorded — {reasonText(feed.fingerprint_reason)}. The editor’s warnings come from each field’s static rule
        alone.
      </Note>
    );
  }
  const run = feed.last_run;
  const later = run && feed.fingerprint && run.import_run_id !== feed.fingerprint.import_run_id;
  return (
    <Note>
      {feed.fingerprint_status === 'backfilled'
        ? 'Reconstructed just now from the latest stored feed XML — the import recorded none.'
        : 'Recorded by the import that last changed what the feed sends.'}
      {later && run?.created_ts
        ? ` The latest feed run (${formatZoned(run.created_ts, tz)}) sent the same shape.`
        : ''}{' '}
      The editor’s restech warnings are built from it.
    </Note>
  );
}

export function FeedSection({
  bookingId,
  timezone,
  tenantId,
}: {
  bookingId: number;
  timezone: string;
  tenantId: number | null;
}) {
  const q = useBookingFeed(bookingId);
  const feed = q.data;
  const name = feed?.managed_by_display ?? feed?.managed_by;
  const meta = feed?.fingerprint ? (
    <Typography variant="caption" color="text.secondary" noWrap>
      {[name, feed.fingerprint.mode?.toUpperCase(), formatZoned(feed.fingerprint.recorded_ts, timezone)]
        .filter(Boolean)
        .join(' · ')}
    </Typography>
  ) : undefined;

  return (
    <TabSection id={FEED_SECTION_ID} title="What the feed sent" action={meta}>
      {q.isLoading ? (
        <TabSkeleton label="Loading what the feed sent" rows={5} />
      ) : q.error || !feed ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void q.refetch()}>
              Retry
            </Button>
          }
        >
          Couldn’t load what the feed sent.
        </Alert>
      ) : (
        // minmax(0, 1fr): the document's long lines scroll inside its viewer instead of
        // widening the card.
        <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 2 }} data-feed-status={feed.fingerprint_status}>
          {feed.booking_id !== feed.requested_booking_id && (
            <Note>
              A party member: the feed sends its lead, so this is booking{' '}
              <Link component={RouterLink} to={`/bookings/${feed.booking_id}/integration`} underline="hover">
                #{feed.booking_id}
              </Link>
              ’s.
            </Note>
          )}
          <StatusNote feed={feed} tz={timezone} />
          {feed.fingerprint && <Fingerprint fp={feed.fingerprint} tz={timezone} tenantId={tenantId} />}
          {feed.last_run && (
            <Typography variant="body2" color="text.secondary" component="p" sx={{ m: 0 }}>
              Latest feed run <RunLink id={feed.last_run.import_run_id} tenantId={tenantId} />
              {feed.last_run.origin ? ` · ${feed.last_run.origin}` : ''}
              {feed.last_run.created_ts ? ` · ${formatZoned(feed.last_run.created_ts, timezone)}` : ''}
            </Typography>
          )}
          <Document doc={feed.document} bookingId={feed.booking_id} />
        </Box>
      )}
    </TabSection>
  );
}
